import { useState, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { get, postMultipart } from "utils/ApiClient";
import { useForm } from "@mantine/form";
import { ProductFormValues } from "types/module/ProductSubmission";
import ProductForm from "components/forms/ProductForm";
import {
	Button,
	Modal,
	Text,
} from "@mantine/core";
import ModalButtons from "components/ui/Modal/ModalButtons";
import { notifications } from '@mantine/notifications';
import { MY_SITE_URL } from "constants/navs";
import { getWordCountFromHtml } from "helpers";

const ProductSubmission: React.FC = () => {
	const { user } = useAuth0();
	const navigate = useNavigate();
	const permissions = Cookies.get("user_permission")?.split("-") || [];
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [loadingCategories, setLoadingCategories] = useState(false);
	const [categoryError, setCategoryError] = useState<string | null>(null);
	const [subCategoryError, setSubCategoryError] = useState<string | null>(null);
	const [categories, setCategories] = useState<Array<{ term_id: number, name: string, }>>([]);
	const [subCategories, setSubCategories] = useState<Array<{ term_id: number, name: string, parent: number }>>([]);
	const formData = new FormData();

	useEffect(() => {
		if (!permissions.includes("productSubmission")) {
			navigate("/restricted");
		}
	}, [permissions, navigate]);

	const form = useForm<ProductFormValues>({
		initialValues: {
			company: Cookies.get("manufacturer") || "",
			brand: Cookies.get("manufacturer") || "",
			productName: "",
			productModelNumber: "",
			productCategory: [],
			productSubCategory: [],
			inputChannels: 0,
			outputChannels: 0,
			bitDepth: [],
			sampleRate: [],
			aes67Enabled: false,
			websiteUrl: "",
			supportLinks: "",
			shortDescription: "",
			longDescription: "",
			submitterName: user?.name || "",
			submitterEmail: user?.email || "",
			submitterId: user?.sub || "",
			danteAvEnabled: false,
			featuredImage: null,
			additionalImages: [],
		},
		validate: {
			brand: (value) => (value ? null : "Brand name is required"),
			productName: (value) => (value ? null : "Product name is required"),
			productCategory: (value) =>
				value && value.length > 0 ? null : "At least one product category is required",
			productSubCategory: () => null,
			shortDescription: (value) => {
				const wordCount = getWordCountFromHtml(value);
				return wordCount <= 50
					? null
					: "Must be 50 words or less";
			},
			longDescription: (value) => {
				const wordCount = getWordCountFromHtml(value);
				if (wordCount === 0) {
					return "Long description is required";
				}
				return wordCount <= 150
					? null
					: "Must be 150 words or less";
			},
			websiteUrl: (value) =>
				!value
					? "Website URL is required"
					: !/^https?:\/\/[^\s$.?#].[^\s]*\.[^\s]{2,}/i.test(value)
						? "Invalid URL format"
						: null,
			supportLinks: (value) =>
				!value
					? "Support link is required"
					: !/^https?:\/\/[^\s$.?#].[^\s]*\.[^\s]{2,}/i.test(value)
						? "Invalid URL format"
						: null,
			featuredImage: (value) => (value ? null : "Featured Image is required"),
		},
	});

	const handleSubmit = async (values: ProductFormValues) => {
		try {
			setLoading(true);

			Object.entries(values).forEach(([key, value]) => {
				if (key !== "featuredImage" && key !== "additionalImages") {
					if (key === "productCategory" || key === "productSubCategory") {
						formData.append(key, JSON.stringify(value));
					} else {
						formData.append(key, value as any);
					}
				}
			});

			if (values.featuredImage) {
				formData.append("featuredImage", values.featuredImage);
			}

			if (values.additionalImages?.length) {
				values.additionalImages.forEach((file) => {
					formData.append("additionalImages", file);
				});
			}

			const response = await postMultipart("/product", formData);

			if (response.data.status === "success") {
				setModalOpen(true);
				form.reset();
			} else {
				notifications.show({
					title: 'Submission Failed',
					message: 'Failed to submit product. Please try again.',
					color: 'red',
				});
				throw new Error(response.data.message || "Failed to submit product");
			}
		} catch (err) {
			setError("Failed to submit product. Please try again.");
			notifications.show({
				title: 'Submission Failed',
				message: 'Failed to submit product. Please try again.',
				color: 'red',
			});
		} finally {
			setLoading(false);
		}
	};

	const fieldRefs = {
		productName: useRef<HTMLInputElement>(null),
		brand: useRef<HTMLInputElement>(null),
		productCategory: useRef<HTMLInputElement>(null),
		longDescription: useRef<HTMLTextAreaElement>(null),
	};

	useEffect(() => {
		const errorKeys = Object.keys(form.errors);

		if (errorKeys.length === 0) return;

		const fieldOrder: (keyof typeof fieldRefs)[] = [
			"productName",
			"brand",
			"productCategory",
			"longDescription",
		];

		const firstErroredKey = fieldOrder.find((key) => errorKeys.includes(key));

		if (firstErroredKey) {
			const ref = fieldRefs[firstErroredKey];
			if (ref?.current) {
				ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
			}
		}
	}, [form.errors]);

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				setLoadingCategories(true);
				setCategoryError(null);
				const response = await get<{ categories: Array<{ term_id: number; name: string }> }>({
					url: '/product/categories'
				});
				setCategories(response.data.categories);
			} catch (err) {
				setCategoryError('Failed to load product categories');
				console.error('Error fetching categories:', err);
			} finally {
				setLoadingCategories(false);
			}
		};

		fetchCategories();
	}, []);

	useEffect(() => {
		const fetchSubCategories = async () => {
			const selectedCategoryIds = form.values.productCategory;

			if (selectedCategoryIds.length === 0) {
				setSubCategories([]);
				return;
			}

			try {
				setLoadingCategories(true);
				setSubCategoryError(null);

				const subcategoryPromises = selectedCategoryIds.map(async (categoryId) => {
					try {
						const response = await get<{ categories: Array<{ term_id: number; name: string }> }>({
							url: `/product/categories?parent_id=${categoryId}`,
						});
						return {
							parentId: categoryId,
							subcategories: response.data.categories || [],
						};
					} catch (error) {
						console.error(`Error fetching subcategories for category ${categoryId}:`, error);
						return {
							parentId: categoryId,
							subcategories: [],
						};
					}
				});

				const results = await Promise.all(subcategoryPromises);

				const allSubCategories: Array<{ term_id: number; name: string; parent: number }> = [];
				const parentMap: Record<string, number> = {};

				results.forEach(({ parentId, subcategories }) => {
					subcategories.forEach((subcat) => {
						allSubCategories.push({
							...subcat,
							parent: Number(parentId),
						});
						parentMap[String(subcat.term_id)] = Number(parentId);
					});
				});

				setSubCategories(allSubCategories);

				const currentSubCategoryIds = form.values.productSubCategory;
				const validSubCategories = currentSubCategoryIds.filter((subCatId) => {
					const parentId = parentMap[subCatId];
					return selectedCategoryIds.includes(String(parentId));
				});

				if (validSubCategories.length !== currentSubCategoryIds.length) {
					form.setFieldValue("productSubCategory", validSubCategories);
				}

			} catch (err) {
				setSubCategoryError('Failed to load product sub-categories');
				console.error("Error fetching subcategories:", err);
			} finally {
				setLoadingCategories(false);
			}
		};

		fetchSubCategories();
	}, [form.values.productCategory.join(','), categories.length]);

	const productCategories = categories.map((cat) => ({
		value: String(cat.term_id),
		label: cat.name,
	}));

	const categoryIdToName: Record<string, string> = {};
	categories.forEach((cat) => {
		categoryIdToName[String(cat.term_id)] = cat.name;
	});

	// Add parent category name as indicator to subcategories with visual separator
	const subcategoryOptions = subCategories.map((cat) => ({
		value: String(cat.term_id),
		label: `${cat.name} • ${categoryIdToName[String(cat.parent)] || 'Unknown Category'}`,
	}));

	return (
		<>
			<ProductForm
				form={form}
				handleSubmit={handleSubmit}
				loading={loading}
				error={error}
				setError={setError}
				productCategories={productCategories}
				subCategories={subcategoryOptions}
				fieldRefs={fieldRefs}
				loadingCategories={loadingCategories}
				categoryError={categoryError}
				subCategoryError={subCategoryError}
			/>
			<Modal
				opened={modalOpen}
				onClose={() => setModalOpen(false)}
				title="Product Submission Successul"
				centered
			>
				<Text>Would you like to upload another product?</Text>

				<ModalButtons>
					<Button
						onClick={() => {
							form.reset();
							setModalOpen(false);
						}}
					>
						Yes
					</Button>

					<Button variant="default" onClick={() => window.location.replace(MY_SITE_URL)}>
						No
					</Button>
				</ModalButtons>
			</Modal>
		</>
	);
};

export default ProductSubmission;
